import { useEffect, useMemo, useRef } from 'react';
import { resumedThumbnails } from '../components/BulkUpload/utils/resumedThumbnails';

/**
 * Fills in thumbnails for pages whose bytes the client no longer holds.
 *
 * A restored page has a url and a placeholder; a freshly uploaded one may have
 * failed its local render. Both end up here, and anything that cannot be drawn
 * keeps its placeholder rather than blocking the step.
 *
 * @param {Array} pages the page list on screen
 * @param {Function} applyThumbnails called with a {pageId: thumbnail} map — the
 *   caller decides which state atoms hold pages, so this hook never has to know.
 */
export const useResumedThumbnails = (pages, applyThumbnails) => {
  // Ids are what change when the page SET changes; a thumbnail landing rewrites
  // the array but not this key, which is what stops the effect re-triggering
  // itself.
  const pageIdKey = useMemo(
    () => (pages || []).map((page) => page.id).join('|'),
    [pages],
  );
  // Read through a ref so the effect can use the live pages without listing the
  // array itself as a dependency it would then re-run on.
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const applyRef = useRef(applyThumbnails);
  applyRef.current = applyThumbnails;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // A page that failed once fails again for the same reason — a dead url, CORS,
  // a corrupt file. Without this, every later upload re-attempts every previous
  // failure, which for whole-file downloads is quadratic in files added.
  const attemptedRef = useRef(new Set());

  useEffect(() => {
    const pending = (pagesRef.current || []).filter(
      (page) => !attemptedRef.current.has(page.id),
    );
    if (pending.length === 0) return;
    pending.forEach((page) => attemptedRef.current.add(page.id));

    resumedThumbnails(pending).then((byPageId) => {
      if (!mountedRef.current) return;
      // Page assignment/reordering or a later upload must not discard previews
      // already rendering. Only the wizard's unmount ends their lifetime; filter
      // against the current pages so deleting a page cannot restore its preview.
      const currentIds = new Set((pagesRef.current || []).map((page) => page.id));
      const retained = Object.fromEntries(
        Object.entries(byPageId).filter(([id]) => currentIds.has(id)),
      );
      if (Object.keys(retained).length) applyRef.current?.(retained);
    });
  }, [pageIdKey]);
};
